import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompletionCelebration = ({
  isVisible,
  onClose,
  lessonTitle,
  nextItem,
  onNextItem,
  onViewCertificate,
  courseProgress,
}) => {
  const nextIsQuiz = nextItem?.itemType === 'quiz';
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    color: ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']?.[i % 5],
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    x: Math.random() * 100,
  }));

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          {/* Confetti Animation */}
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {confettiPieces?.map((piece) => (
                <motion.div
                  key={piece?.id}
                  initial={{ 
                    y: -20, 
                    x: `${piece?.x}vw`, 
                    rotate: 0, 
                    opacity: 1 
                  }}
                  animate={{ 
                    y: '100vh', 
                    rotate: 360, 
                    opacity: 0 
                  }}
                  transition={{
                    duration: piece?.duration,
                    delay: piece?.delay,
                    ease: 'easeOut'
                  }}
                  className="absolute w-3 h-3 rounded-full"
                  style={{ backgroundColor: piece?.color }}
                />
              ))}
            </div>
          )}

          {/* Celebration Modal */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="bg-card rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-2xl"
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 15 }}
              className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Icon name="CheckCircle" size={40} color="white" />
            </motion.div>

            {/* Congratulations Text */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="space-y-4 mb-8"
            >
              <h2 className="text-2xl font-bold text-foreground">
                Congratulations! 🎉
              </h2>
              <p className="text-muted-foreground">
                You've successfully completed
              </p>
              <h3 className="text-lg font-semibold text-accent">
                "{lessonTitle}"
              </h3>
            </motion.div>

            {/* Progress Stats */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-muted/50 rounded-lg p-4 mb-6"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Course Progress</span>
                <span className="text-sm font-semibold text-foreground">
                  {Math.round(courseProgress)}%
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${courseProgress}%` }}
                  transition={{ delay: 0.5, duration: 1 }}
                  className="bg-success h-2 rounded-full"
                />
              </div>
            </motion.div>

            {/* Achievement Badges */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex justify-center space-x-4 mb-8"
            >
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mb-2">
                  <Icon name="Trophy" size={20} className="text-warning" />
                </div>
                <span className="text-xs text-muted-foreground">Achievement</span>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mb-2">
                  <Icon name="Star" size={20} className="text-accent" />
                </div>
                <span className="text-xs text-muted-foreground">Progress</span>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mb-2">
                  <Icon name="Award" size={20} className="text-success" />
                </div>
                <span className="text-xs text-muted-foreground">Completed</span>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="space-y-3"
            >
              {nextItem ? (
                <Button
                  variant="default"
                  fullWidth
                  onClick={onNextItem}
                  iconName={nextIsQuiz ? 'ListChecks' : 'ArrowRight'}
                  iconPosition="right"
                >
                  {nextIsQuiz ? 'Take Module Quiz' : 'Continue to Next Lesson'}
                </Button>
              ) : (
                <Button
                  variant="default"
                  fullWidth
                  onClick={onViewCertificate}
                  iconName="Trophy"
                  iconPosition="left"
                >
                  Go to Capstone Exam
                </Button>
              )}
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                >
                  Close
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  iconName="Share2"
                  iconPosition="left"
                >
                  Share
                </Button>
              </div>
            </motion.div>

            {/* Fun Fact or Tip */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-6 p-3 bg-accent/5 rounded-lg border border-accent/10"
            >
              <div className="flex items-start space-x-2">
                <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
                <div className="text-left">
                  <p className="text-xs font-medium text-accent mb-1">Pro Tip</p>
                  <p className="text-xs text-muted-foreground">
                    Review your notes and practice what you've learned to reinforce your knowledge!
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompletionCelebration;